import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { playChime, initSoundUnlock } from "@/lib/sound";
import { toast } from "sonner";
import { MapPin, Clock, Briefcase, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const NEEDS_ACCEPT = "PROFESSIONAL ASSIGNED";

// Ride-app style full-screen "New job" popup for professionals.
export function ProJobAlert() {
    const { user } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const declined = useRef(new Set()); // jobs the pro already acted on this session
    const vibrateTimer = useRef(null);

    useEffect(() => { initSoundUnlock(); }, []);

    const stopVibrate = () => {
        if (vibrateTimer.current) { clearInterval(vibrateTimer.current); vibrateTimer.current = null; }
        try { if (navigator.vibrate) navigator.vibrate(0); } catch (e) { /* no-op */ }
    };

    const poll = useCallback(async () => {
        try {
            if (navigator.geolocation && navigator.permissions) {
                const p = await navigator.permissions.query({ name: "geolocation" }).catch(() => null);
                if (p?.state === "granted") {
                    navigator.geolocation.getCurrentPosition((pos) => {
                        api.post("/pro/location", { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }).catch(() => {});
                    }, () => {}, { maximumAge: 60000, timeout: 8000 });
                }
            }
            const [{ data }, offers, hc] = await Promise.all([api.get("/bookings"), api.get("/pro/offers").then((r) => r.data).catch(() => []), api.get("/pro/healthcare/offers").then((r) => r.data).catch(() => [])]);
            const list = [
                ...(hc || []).filter((o) => !declined.current.has(o.id)).map((o) => ({ id: o.id, offer: true, hc: true, service_name: o.kind_label, booking_id: o.ref, area_name: o.area_name, preferred_date: o.date, preferred_window: o.slot, distance_km: o.distance_km, problem: o.earning ? `You earn ₹${o.earning}` : null })),
                ...(data || []).filter((b) => b.status === NEEDS_ACCEPT && !declined.current.has(b.id)),
                ...(offers || []).filter((o) => !declined.current.has(o.id)).map((o) => ({ ...o, offer: true })),
            ];
            setJob((cur) => {
                if (cur && (!cur.offer || list.some((x) => x.id === cur.id))) return cur; // keep showing the current one while still available
                return list[0] || null;
            });
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        if (!user || user.role !== "professional") { setJob(null); return; }
        poll();
        const t = setInterval(poll, 12000);
        return () => { clearInterval(t); stopVibrate(); };
    }, [user?.id, user?.role, poll]);

    // ring + vibrate while a job is pending
    useEffect(() => {
        if (!job) { stopVibrate(); return; }
        playChime("professional", true, "services");
        if (navigator.vibrate) {
            try {
                navigator.vibrate([400, 200, 400]);
                vibrateTimer.current = setInterval(() => { try { navigator.vibrate([400, 200, 400]); } catch (e) { /* no-op */ } }, 2500);
            } catch (e) { /* vibrate blocked until user gesture — ignore */ }
        }
        return () => stopVibrate();
    }, [job?.id]); // eslint-disable-line

    if (!user || user.role !== "professional" || !job) return null;

    const act = async (action) => {
        setBusy(true);
        stopVibrate();
        try {
            await api.post(job.hc ? `/pro/healthcare/offers/${job.id}/${action === "accept" ? "accept" : "reject"}` : job.offer ? `/pro/offers/${job.id}/${action === "accept" ? "accept" : "decline"}` : `/pro/jobs/${job.id}/${action}`);
            declined.current.add(job.id);
            if (action === "accept" && job.hc) {
                toast.success("Nursing visit accepted. Patient details are under Nursing visits.");
                setJob(null);
                nav("/pro");
            } else if (action === "accept") {
                toast.success("Job accepted — opening details");
                const id = job.id;
                setJob(null);
                nav(`/pro/jobs/${id}`);
            } else {
                toast("Job declined");
                setJob(null);
                setTimeout(poll, 500);
            }
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail) || "Could not update job");
            if (job.offer) { declined.current.add(job.id); setJob(null); }
        } finally { setBusy(false); }
    };

    const when = [job.preferred_date, job.preferred_window].filter(Boolean).join(" · ");
    const where = [job.society, job.address_line, job.area_name].filter(Boolean).join(", ");
    const later = () => { stopVibrate(); declined.current.add(job.id); setJob(null); };

    // While working a job, nearby offers show as a compact banner instead of blocking the screen
    if (job.offer && loc.pathname.startsWith("/pro/jobs/")) {
        return (
            <div className="fixed inset-x-0 top-[92px] z-[80] mx-auto w-[calc(100%-1.5rem)] max-w-md rounded-2xl bg-navy p-3 text-white shadow-2xl ring-2 ring-emerald-400" data-testid="pro-offer-banner">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">Nearby job · {job.distance_km} km</p>
                <p className="text-sm font-bold">{job.service_name} · {job.area_name}</p><p className="text-[11px] text-white/60">{job.booking_id}{job.problem ? ` · ${job.problem}` : ""}</p>
                <div className="mt-2 flex gap-2">
                    <button data-testid="pro-offer-later-btn" onClick={later} className="flex-1 rounded-xl border border-white/20 py-2 text-xs font-bold">Later</button>
                    <button data-testid="pro-offer-decline-btn" disabled={busy} onClick={() => act("reject")} className="flex-1 rounded-xl border border-white/20 py-2 text-xs font-bold">Decline</button>
                    <button data-testid="pro-offer-accept-btn" disabled={busy} onClick={() => act("accept")} className="flex-1 rounded-xl bg-emerald-500 py-2 text-xs font-bold">Accept</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-navy/95 px-6 py-10 backdrop-blur-sm" data-testid="pro-job-alert">
            <span className="mb-6 flex h-3 w-3 items-center justify-center">
                <span className="absolute inline-flex h-16 w-16 animate-ping rounded-full bg-emerald-400/30" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">{job.hc ? `Nursing request · ${job.distance_km} km away` : job.offer ? `Nearby job · ${job.distance_km} km away` : "New job request"}</p>
            <h2 className="mt-3 text-center font-heading text-3xl font-extrabold text-white" data-testid="pro-job-alert-service">{job.service_name || "Home service"}</h2>
            <p className="mt-1 text-sm font-semibold text-white/50">{job.booking_id}</p>

            <div className="mt-7 w-full max-w-sm space-y-3 rounded-3xl bg-white/5 p-5 ring-1 ring-white/10">
                {job.problem && (
                    <Row icon={Briefcase} text={job.problem} />
                )}
                {where && <Row icon={MapPin} text={where} />}
                {when && <Row icon={Clock} text={when} />}
            </div>

            <div className="mt-8 grid w-full max-w-sm grid-cols-2 gap-3">
                <button data-testid="pro-job-reject-btn" disabled={busy} onClick={() => act("reject")}
                    className="flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/5 py-4 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-60">
                    <XCircle size={18} /> Decline
                </button>
                <button data-testid="pro-job-accept-btn" disabled={busy} onClick={() => act("accept")}
                    className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-4 text-sm font-bold text-white shadow-lg shadow-emerald-500/30 transition-transform active:scale-95 disabled:opacity-60">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Accept
                </button>
            </div>
            <p className="mt-4 text-center text-[11px] text-white/40">Respond quickly to keep your acceptance rate high.</p>
        </div>
    );
}

function Row({ icon: Icon, text }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={18} className="mt-0.5 shrink-0 text-emerald-300" />
            <span className="text-sm font-medium text-white/85">{text}</span>
        </div>
    );
}
